import { InputBaseComponentProps } from '@mui/material';
import React, { useMemo, ReactElement, forwardRef } from 'react';
import NumberFormat, { NumberFormatProps, NumberFormatValues } from 'react-number-format';

import { TextField } from '../text-field';
import { TextFieldProps } from '../text-field/TextField';

type NumberFormatFieldProps = TextFieldProps & {
  format?: NumberFormatProps<unknown>;
  onValueChange?: (values: NumberFormatValues) => void;
};

type NumberFormatInputProps = {
  inputRef: (instance: NumberFormat<unknown> | null) => void;
};

const makeNumberFormatInput: (
  format?: NumberFormatProps<unknown>,
  onValueChange?: (values: NumberFormatValues) => void,
) => React.FC<NumberFormatInputProps> = (format, onValueChange) => {
  const NumberFormatInput: React.FC<NumberFormatInputProps> = forwardRef((props: NumberFormatInputProps, ref) => {
    const { ...rest } = props;
    return <NumberFormat {...rest} {...format} onValueChange={onValueChange} getInputRef={ref} />;
  });
  NumberFormatInput.displayName = 'NumberFormatInput';
  return NumberFormatInput;
};

const NumberFormatField = (props: NumberFormatFieldProps): ReactElement => {
  const { format, onValueChange, InputProps, ...rest } = props;

  const NumberFormatInput: React.FC<NumberFormatInputProps> = useMemo(() => {
    return makeNumberFormatInput(format, onValueChange);
  }, [format, onValueChange]);

  const inputProps: TextFieldProps['InputProps'] = useMemo(
    () => ({
      ...InputProps,
      inputComponent: NumberFormatInput as React.ElementType<InputBaseComponentProps>,
    }),
    [InputProps, NumberFormatInput],
  );
  return <TextField {...rest} InputProps={inputProps} />;
};

export { NumberFormatField, makeNumberFormatInput };
